import { getStoredUser, apiGet, apiPost, apiPut, apiDelete } from './api';
import { verifyUserEmailExists } from './contactsStorage';

export interface ShoppingItem {
  id: string;
  title: string;
  quantity?: string;
  category: 'produce' | 'dairy' | 'grocery' | 'meat' | 'household' | 'other';
  bought: boolean;
  createdAt: string;
  boughtAt?: string;
  updatedByEmail?: string;
}

export interface ShoppingList {
  id: string;
  name: string; // e.g. "خرید هفتگی خانه", "کباب و دورهمی", "داروخانه"
  ownerUid: string;
  ownerEmail: string;
  ownerName?: string;
  sharedWithEmails: string[]; // List of lowercased emails
  sharedWithUids: string[]; // List of uids
  items: ShoppingItem[];
  color?: string; // Theme accent e.g. 'emerald', 'rose', 'indigo', 'amber', 'teal'
  icon?: string;
  purchaseDate?: string; // YYYY-MM-DD format for purchase schedule/record
  createdAt: string;
  updatedAt: string;
}

export const CATEGORY_LABELS: Record<ShoppingItem['category'], string> = {
  produce: 'میوه و سبزیجات',
  dairy: 'لبنیات و پروتئین',
  grocery: 'خواربار و سوپرمارکت',
  meat: 'گوشت و مرغ',
  household: 'لوازم خانه و بهداشتی',
  other: 'متفرقه'
};

export function getCategoryLabel(category: ShoppingItem['category'], lang: string): string {
  if (lang === 'sv') {
    const svMap: Record<ShoppingItem['category'], string> = {
      produce: 'Frukt & Grönsaker',
      dairy: 'Mejeri & Frukost',
      grocery: 'Skafferi & Torrvaror',
      meat: 'Kött, Kyckling & Fisk',
      household: 'Hushåll & Hygien',
      other: 'Övrigt'
    };
    return svMap[category] || category;
  }
  if (lang === 'en') {
    const enMap: Record<ShoppingItem['category'], string> = {
      produce: 'Fruits & Vegetables',
      dairy: 'Dairy & Breakfast',
      grocery: 'Groceries & Pantry',
      meat: 'Meat, Poultry & Seafood',
      household: 'Household & Hygiene',
      other: 'Other'
    };
    return enMap[category] || category;
  }
  return CATEGORY_LABELS[category] || category;
}

export interface QuickPresetItem {
  title: string;
  enTitle?: string;
  svTitle?: string;
  category: ShoppingItem['category'];
  quantity: string;
  enQuantity?: string;
  svQuantity?: string;
}

export const QUICK_PRESETS: QuickPresetItem[] = [
  // Meat, Poultry & Seafood
  { title: 'مرغ کامل', enTitle: 'Whole Chicken', svTitle: 'Hel kyckling', category: 'meat', quantity: '۱ عدد', enQuantity: '1 pcs', svQuantity: '1 st' },
  { title: 'فیله مرغ', enTitle: 'Chicken Breast Fillet', svTitle: 'Kycklingfilé', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'سینه مرغ', enTitle: 'Chicken Breast', svTitle: 'Kycklingbröst', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'ران مرغ', enTitle: 'Chicken Thighs', svTitle: 'Kycklinglår', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'بال و گردن مرغ', enTitle: 'Chicken Wings & Necks', svTitle: 'Kycklingvingar', category: 'meat', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'گوشت چرخ‌کرده', enTitle: 'Ground Beef / Minced Meat', svTitle: 'Nötfärs / Blandfärs', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'گوشت خورشتی (گوسفندی/گوساله)', enTitle: 'Beef Stew Meat / Lamb Cubes', svTitle: 'Grytbitar av nötkött/lamm', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'گوشت آبگوشتی / با استخوان', enTitle: 'Soup Bones / Lamb Chops', svTitle: 'Lammracks / Soppkött med ben', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'بوقلمون (فیله/چرخ‌کرده)', enTitle: 'Turkey Fillet / Ground Turkey', svTitle: 'Kalkonfilé / Kalkonfärs', category: 'meat', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'ماهی قزل‌آلا / سالمون', enTitle: 'Salmon / Trout Fillet', svTitle: 'Laxfilé / Regnbåge', category: 'meat', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'میگو پاک‌شده', enTitle: 'Peeled Shrimps', svTitle: 'Skalade räkor', category: 'meat', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'سوسیس / کالباس', enTitle: 'Sausages / Cold Cuts', svTitle: 'Korv / Pålägg', category: 'meat', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },

  // Produce: Vegetables & Herbs
  { title: 'سیب‌زمینی', enTitle: 'Potatoes', svTitle: 'Potatis', category: 'produce', quantity: '۲ کیلو', enQuantity: '2 kg', svQuantity: '2 kg' },
  { title: 'پیاز (زرد/قرمز)', enTitle: 'Onions (Yellow/Red)', svTitle: 'Lök (Gul/Röd)', category: 'produce', quantity: '۲ کیلو', enQuantity: '2 kg', svQuantity: '2 kg' },
  { title: 'گوجه‌فرنگی', enTitle: 'Tomatoes', svTitle: 'Tomater', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'خیار بوته‌ای / گلخانه‌ای', enTitle: 'Cucumbers', svTitle: 'Gurka', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'کاهو (رسمی/سالادی)', enTitle: 'Lettuce / Iceberg', svTitle: 'Sallad / Isbergssallad', category: 'produce', quantity: '۱ بوته', enQuantity: '1 head', svQuantity: '1 st' },
  { title: 'کلم بروکلی', enTitle: 'Broccoli', svTitle: 'Broccoli', category: 'produce', quantity: '۱ بوته', enQuantity: '1 head', svQuantity: '1 st' },
  { title: 'هویج', enTitle: 'Carrots', svTitle: 'Morötter', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'فلفل دلمه‌ای (رنگی/سبز)', enTitle: 'Bell Peppers', svTitle: 'Paprika (Mix)', category: 'produce', quantity: 'نیم کیلو', enQuantity: '0.5 kg', svQuantity: '0.5 kg' },
  { title: 'بادمجان', enTitle: 'Eggplants / Aubergine', svTitle: 'Aubergine / Mjölkrot', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'کدو سبز', enTitle: 'Zucchini / Courgette', svTitle: 'Zucchini', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'قارچ دکمه‌ای', enTitle: 'Button Mushrooms', svTitle: 'Champinjoner', category: 'produce', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'اسفناج تازه', enTitle: 'Fresh Spinach', svTitle: 'Färsk spenat', category: 'produce', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'کرفس', enTitle: 'Celery', svTitle: 'Selleri', category: 'produce', quantity: '۱ بوته', enQuantity: '1 stalk', svQuantity: '1 st' },
  { title: 'گل کلم', enTitle: 'Cauliflower', svTitle: 'Blomkål', category: 'produce', quantity: '۱ عدد', enQuantity: '1 head', svQuantity: '1 st' },
  { title: 'سبزی خوردن / تازه', enTitle: 'Fresh Herbs Salad', svTitle: 'Färska örter / Salladsörter', category: 'produce', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'سبزی قورمه/آش/پلو', enTitle: 'Stew / Soup Herbs Mix', svTitle: 'Örtblandning för gryta', category: 'produce', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'سیر تازه', enTitle: 'Fresh Garlic', svTitle: 'Färsk vitlök', category: 'produce', quantity: '۱ بته', enQuantity: '1 bulb', svQuantity: '1 st' },
  { title: 'لیموترش تازه', enTitle: 'Fresh Lemons / Limes', svTitle: 'Citron / Lime', category: 'produce', quantity: 'نیم کیلو', enQuantity: '0.5 kg', svQuantity: '0.5 kg' },

  // Produce: Fruits
  { title: 'موز', enTitle: 'Bananas', svTitle: 'Bananer', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'سیب (درختی/قرمز/زرد)', enTitle: 'Apples (Red/Yellow)', svTitle: 'Äpplen (Röda/Gula)', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'پرتقال / تامسون', enTitle: 'Oranges', svTitle: 'Apelsiner', category: 'produce', quantity: '۲ کیلو', enQuantity: '2 kg', svQuantity: '2 kg' },
  { title: 'نارنگی', enTitle: 'Clementines / Mandarins', svTitle: 'Klementiner / Mandariner', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'لیمو شیرین', enTitle: 'Sweet Lemons', svTitle: 'Söta citroner', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'انگور', enTitle: 'Grapes', svTitle: 'Druvor', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'هندوانه', enTitle: 'Watermelon', svTitle: 'Vattenmelon', category: 'produce', quantity: '۱ عدد', enQuantity: '1 pcs', svQuantity: '1 st' },
  { title: 'خربزه / طالبی', enTitle: 'Melon / Cantaloupe', svTitle: 'Melon / Cantaloupe', category: 'produce', quantity: '۱ عدد', enQuantity: '1 pcs', svQuantity: '1 st' },
  { title: 'توت‌فرنگی', enTitle: 'Strawberries', svTitle: 'Jordgubbar', category: 'produce', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'هلو / شلیل', enTitle: 'Peaches / Nectarines', svTitle: 'Persikor / Nektariner', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'کیوی', enTitle: 'Kiwis', svTitle: 'Kivi', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },
  { title: 'انار', enTitle: 'Pomegranates', svTitle: 'Granatäpplen', category: 'produce', quantity: '۱ کیلو', enQuantity: '1 kg', svQuantity: '1 kg' },

  // Dairy, Eggs & Breakfast
  { title: 'شیر (کم‌چرب/پرچرب)', enTitle: 'Milk (Low-fat/Whole)', svTitle: 'Mjölk (Mellan/Hel)', category: 'dairy', quantity: '۱ بطری', enQuantity: '1 bottle/liter', svQuantity: '1 lit' },
  { title: 'تخم‌مرغ', enTitle: 'Eggs', svTitle: 'Ägg', category: 'dairy', quantity: '۱ شانه', enQuantity: '1 carton (12 pcs)', svQuantity: '1 förp (12-pack)' },
  { title: 'پنیر (سفید/لاکتیکی/تبریزی)', enTitle: 'White Cheese / Feta', svTitle: 'Salladsost / Feta', category: 'dairy', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'پنیر خامه‌ای / خامه‌ای لبنه', enTitle: 'Cream Cheese / Labneh', svTitle: 'Färskost / Labneh', category: 'dairy', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 برای' },
  { title: 'ماست (همزده/چکیده/دبه)', enTitle: 'Yogurt / Greek Yogurt', svTitle: 'Yoghurt / Turkisk Yoghurt', category: 'dairy', quantity: '۱ دبه', enQuantity: '1 tub', svQuantity: '1 hink' },
  { title: 'کره پاستوریزه', enTitle: 'Butter', svTitle: 'Smör / Mjölkfritt Smör', category: 'dairy', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'خامه صبحانه', enTitle: 'Breakfast Cream / Sour Cream', svTitle: 'Grädde / Vispgrädde', category: 'dairy', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'عسل طبیعی', enTitle: 'Natural Honey', svTitle: 'Naturlig Honung', category: 'dairy', quantity: '۱ شیشه', enQuantity: '1 jar', svQuantity: '1 burk' },
  { title: 'مربا (هویج/آلبالو/توت‌فرنگی)', enTitle: 'Fruit Jam', svTitle: 'Sylt (Jordgubb/Körsbär)', category: 'dairy', quantity: '۱ شیشه', enQuantity: '1 jar', svQuantity: '1 burk' },
  { title: 'کره بادام‌زمینی / ارده', enTitle: 'Peanut Butter / Tahini', svTitle: 'Jordnötssmör / Tahini', category: 'dairy', quantity: '۱ عدد', enQuantity: '1 jar', svQuantity: '1 burk' },

  // Grocery, Rice & Pantry
  { title: 'برنج ایرانی (هاشمی/طارم)', enTitle: 'Basmati Rice', svTitle: 'Basmatiris', category: 'grocery', quantity: '۵ کیلو', enQuantity: '5 kg', svQuantity: '5 kg' },
  { title: 'برنج قهوه‌ای / رژیمی', enTitle: 'Brown Rice', svTitle: 'Råris / Brunt ris', category: 'grocery', quantity: '۲ کیلو', enQuantity: '2 kg', svQuantity: '2 kg' },
  { title: 'برنج هندی / پاکستانی', enTitle: 'Long Grain Rice', svTitle: 'Långkornigt ris', category: 'grocery', quantity: '۵ کیلو', enQuantity: '5 kg', svQuantity: '5 kg' },
  { title: 'نان (سنگک/بربری/لواش/تافتون)', enTitle: 'Bread (Flatbread/Pita)', svTitle: 'Bröd / Tunnbröd', category: 'grocery', quantity: '۳ عدد', enQuantity: '3 pcs', svQuantity: '3 st' },
  { title: 'نان تست / جو', enTitle: 'Toast / Oat Bread', svTitle: 'Rostbröd / Havrebröd', category: 'grocery', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'روغن سرخ‌کردنی / پخت‌وپز', enTitle: 'Cooking / Frying Oil', svTitle: 'Matolja / Stekolja', category: 'grocery', quantity: '۱ بطری', enQuantity: '1 bottle', svQuantity: '1 flask' },
  { title: 'روغن زیتون (فرابکر/تصفیه)', enTitle: 'Extra Virgin Olive Oil', svTitle: 'Extra Jungfruolivolja', category: 'grocery', quantity: '۱ بطری', enQuantity: '1 bottle', svQuantity: '1 flask' },
  { title: 'رب گوجه‌فرنگی', enTitle: 'Tomato Paste', svTitle: 'Tomatpuré', category: 'grocery', quantity: '۱ قوطی', enQuantity: '1 can', svQuantity: '1 burk' },
  { title: 'چای سیاه / سبز', enTitle: 'Black / Green Tea', svTitle: 'Svart / Grönt Te', category: 'grocery', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'قهوه / اسپرسو / نسکافه', enTitle: 'Coffee / Espresso', svTitle: 'Kaffe / Espresso', category: 'grocery', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'ماکارونی / پستا', enTitle: 'Pasta / Spaghetti', svTitle: 'Pasta / Spaghetti', category: 'grocery', quantity: '۲ بسته', enQuantity: '2 packs', svQuantity: '2 förp' },
  { title: 'عدس / لپه / نخود / لوبیا', enTitle: 'Lentils / Chickpeas / Beans', svTitle: 'Linser / Kikärtor / Bönor', category: 'grocery', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'ادویه‌جات (زردچوبه/فلفل/دارچین)', enTitle: 'Spices (Turmeric/Pepper)', svTitle: 'Kryddor (Gurkmeja/Peppar)', category: 'grocery', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'زعفران', enTitle: 'Saffron', svTitle: 'Saffran', category: 'grocery', quantity: '۱ مثقال', enQuantity: '1 gram', svQuantity: '1 gram' },
  { title: 'آبلیمو / آبغوره / سرکه', enTitle: 'Lemon Juice / Vinegar', svTitle: 'Citronsaft / Vinäger', category: 'grocery', quantity: '۱ بطری', enQuantity: '1 bottle', svQuantity: '1 flask' },

  // Household & Hygiene
  { title: 'دستمال کاغذی / دلسی', enTitle: 'Tissue / Toilet Paper', svTitle: 'Hushållspapper / Toapapper', category: 'household', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'مایع ظرفشویی', enTitle: 'Dishwashing Liquid', svTitle: 'Diskmedel', category: 'household', quantity: '۱ بطری', enQuantity: '1 bottle', svQuantity: '1 flask' },
  { title: 'مایع دستشویی', enTitle: 'Hand Soap', svTitle: 'Handtvål', category: 'household', quantity: '۱ بطری', enQuantity: '1 bottle', svQuantity: '1 flask' },
  { title: 'پودر / ژل لباسشویی', enTitle: 'Laundry Detergent', svTitle: 'Tvättmedel', category: 'household', quantity: '۱ عدد', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'کیسه زباله / فریزر', enTitle: 'Trash / Freezer Bags', svTitle: 'Avfallspåsar / Fryspåsar', category: 'household', quantity: '۱ بسته', enQuantity: '1 pack', svQuantity: '1 förp' },
  { title: 'اسکاج / سیم ظرفشویی', enTitle: 'Sponge / Scourer', svTitle: 'Disksvamp / Stålull', category: 'household', quantity: '۱ عدد', enQuantity: '1 pcs', svQuantity: '1 st' }
];

export function getLocalizedPresetItem(item: QuickPresetItem, lang: string): { title: string; category: ShoppingItem['category']; quantity: string } {
  if (lang === 'sv') {
    return {
      title: item.svTitle || item.enTitle || item.title,
      category: item.category,
      quantity: item.svQuantity || item.enQuantity || item.quantity
    };
  }
  if (lang === 'en') {
    return {
      title: item.enTitle || item.title,
      category: item.category,
      quantity: item.enQuantity || item.quantity
    };
  }
  return {
    title: item.title,
    category: item.category,
    quantity: item.quantity
  };
}

let inMemoryShoppingLists: ShoppingList[] = [];
const shoppingListListeners = new Set<(lists: ShoppingList[]) => void>();

function notifySubscribers(): void {
  const currentSnapshot = [...inMemoryShoppingLists];
  shoppingListListeners.forEach(callback => {
    try {
      callback(currentSnapshot);
    } catch (err) {
      console.error('Error notifying shopping list subscriber:', err);
    }
  });
}

export function isListOwner(list: ShoppingList, user = getStoredUser()): boolean {
  if (!list) return false;
  if (!user) {
    return list.ownerUid === 'guest_user';
  }
  if (user.id && (list.ownerUid === user.id || (list as any).owner_id === user.id)) return true;
  if (user.email && list.ownerEmail && user.email.toLowerCase().trim() === list.ownerEmail.toLowerCase().trim()) return true;
  return false;
}

export function getShoppingListsFromMemory(): ShoppingList[] {
  return [...inMemoryShoppingLists];
}

// Fetch all lists from Cloudflare D1 and populate memory
export async function fetchShoppingListsFromServer(): Promise<ShoppingList[]> {
  const user = getStoredUser();
  if (!user) {
    inMemoryShoppingLists = [];
    notifySubscribers();
    return [];
  }

  try {
    const listRes = await apiGet<any[]>('/api/shopping/lists');
    if (listRes.success && Array.isArray(listRes.data)) {
      const fullLists: ShoppingList[] = await Promise.all(
        listRes.data.map(async (rawList) => {
          // Fetch items of the list in parallel
          let items: ShoppingItem[] = [];
          try {
            const itemsRes = await apiGet<any[]>(`/api/shopping/lists/${rawList.id}/items`);
            if (itemsRes.success && Array.isArray(itemsRes.data)) {
              items = itemsRes.data.map(i => ({
                id: i.id,
                title: i.item_name || i.itemName || i.title || '',
                quantity: i.unit || (i.quantity ? String(i.quantity) : ''),
                category: i.category || 'other',
                bought: Boolean(i.is_bought || i.isBought || i.bought),
                createdAt: i.created_at || new Date().toISOString()
              }));
            }
          } catch (e) {
            console.warn(`Failed to fetch items for list ${rawList.id}:`, e);
          }

          // Fetch shares of the list if owner
          let sharedWithEmails: string[] = Array.isArray(rawList.sharedWithEmails) 
            ? rawList.sharedWithEmails 
            : (Array.isArray(rawList.shared_with) ? rawList.shared_with : []);

          const isOwner = rawList.ownerUid === user.id || 
                          rawList.owner_id === user.id || 
                          Boolean(rawList.is_owner) || 
                          Boolean(rawList.isOwner) ||
                          (rawList.ownerEmail && user.email && rawList.ownerEmail.toLowerCase().trim() === user.email.toLowerCase().trim());

          if (isOwner) {
            try {
              const shareRes = await apiGet<any[]>(`/api/shopping/lists/${rawList.id}/shares`);
              if (shareRes.success && Array.isArray(shareRes.data)) {
                const fetchedEmails = shareRes.data.map(s => s.shared_with_email || s.email).filter(Boolean);
                sharedWithEmails = [...new Set([...sharedWithEmails, ...fetchedEmails])];
              }
            } catch (e) {
              // ignore
            }
          }

          return {
            id: rawList.id,
            name: rawList.name,
            ownerUid: rawList.ownerUid || rawList.owner_id || user.id,
            ownerEmail: rawList.ownerEmail || rawList.owner_email || user.email || 'guest@app.com',
            ownerName: rawList.ownerName || rawList.owner_name || user.displayName || '',
            sharedWithEmails,
            sharedWithUids: [],
            items,
            color: rawList.category || 'emerald',
            icon: 'shopping-bag',
            purchaseDate: rawList.purchaseDate || new Date().toISOString().split('T')[0],
            createdAt: rawList.createdAt || rawList.created_at || new Date().toISOString(),
            updatedAt: rawList.updatedAt || rawList.updated_at || new Date().toISOString()
          };
        })
      );

      inMemoryShoppingLists = fullLists;
      notifySubscribers();
      return fullLists;
    }
  } catch (err) {
    console.warn('Error fetching shopping lists from Cloudflare D1:', err);
  }

  return inMemoryShoppingLists;
}

// Save single list document and its items to canonical backend in Cloudflare D1
export async function saveShoppingList(list: ShoppingList): Promise<void> {
  const user = getStoredUser();
  const now = new Date().toISOString();

  const updatedList: ShoppingList = {
    ...list,
    updatedAt: now
  };

  const idx = inMemoryShoppingLists.findIndex(l => l.id === list.id);
  if (idx >= 0) {
    inMemoryShoppingLists[idx] = updatedList;
  } else {
    inMemoryShoppingLists.unshift(updatedList);
  }

  notifySubscribers();

  if (!user) return;

  try {
    // 1. Ensure shopping list metadata is updated
    const putRes = await apiPut(`/api/shopping/lists/${list.id}`, {
      name: list.name,
      category: list.color || 'emerald'
    });

    if (!putRes.success && putRes.status === 404) {
      // Create if missing on server
      await apiPost('/api/shopping/lists', {
        id: list.id,
        name: list.name,
        category: list.color || 'emerald'
      });
    }

    // 2. Fetch current items from server to perform a smart diff
    const currentItemsRes = await apiGet<any[]>(`/api/shopping/lists/${list.id}/items`);
    const currentItems = currentItemsRes.success && Array.isArray(currentItemsRes.data) ? currentItemsRes.data : [];

    const serverItemsMap = new Map<string, any>();
    currentItems.forEach(i => serverItemsMap.set(i.id, i));

    // 3. Add or update items in parallel
    const itemSyncPromises: Promise<any>[] = [];

    for (const item of list.items) {
      const exists = serverItemsMap.get(item.id);
      if (!exists) {
        // Create new item
        itemSyncPromises.push(
          apiPost(`/api/shopping/lists/${list.id}/items`, {
            id: item.id,
            itemName: item.title,
            unit: item.quantity || '',
            is_bought: item.bought,
            category: item.category || 'other'
          })
        );
      } else {
        // Check if anything changed before calling PUT
        const hasChanged = exists.item_name !== item.title ||
                           exists.unit !== (item.quantity || '') ||
                           Boolean(exists.is_bought) !== item.bought ||
                           exists.category !== (item.category || 'other');
        if (hasChanged) {
          itemSyncPromises.push(
            apiPut(`/api/shopping/items/${item.id}`, {
              itemName: item.title,
              unit: item.quantity || '',
              is_bought: item.bought,
              category: item.category || 'other'
            })
          );
        }
      }
    }

    // 4. Delete removed items in parallel
    const clientIds = new Set(list.items.map(i => i.id));
    for (const serverItem of currentItems) {
      if (!clientIds.has(serverItem.id)) {
        itemSyncPromises.push(
          apiDelete(`/api/shopping/items/${serverItem.id}`)
        );
      }
    }

    await Promise.all(itemSyncPromises);
  } catch (err) {
    console.warn('Error saving shopping list & items to server:', err);
  }
}

// Create a new shopping list
export async function createShoppingList(
  name: string, 
  color = 'emerald', 
  icon = 'shopping-bag',
  purchaseDate?: string
): Promise<ShoppingList> {
  const user = getStoredUser();
  const uid = user ? user.id : 'guest_user';
  const email = user ? (user.email || 'guest@app.com') : 'guest@app.com';
  const displayName = user ? (user.displayName || email.split('@')[0]) : 'کاربر مهمان';

  const todayStr = new Date().toISOString().split('T')[0];
  const listId = `list_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  const newList: ShoppingList = {
    id: listId,
    name: name.trim() || 'لیست خرید جدید',
    ownerUid: uid,
    ownerEmail: email.toLowerCase(),
    ownerName: displayName,
    sharedWithEmails: [],
    sharedWithUids: [],
    items: [],
    color,
    icon,
    purchaseDate: purchaseDate || todayStr,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  inMemoryShoppingLists.unshift(newList);
  notifySubscribers();

  if (user) {
    try {
      const res = await apiPost('/api/shopping/lists', {
        id: listId,
        name: newList.name,
        category: color
      });
      if (res.success && res.data?.id) {
        const assignedId = res.data.id;
        if (assignedId !== listId) {
          newList.id = assignedId;
          const idx = inMemoryShoppingLists.findIndex(l => l.id === listId);
          if (idx >= 0) inMemoryShoppingLists[idx] = newList;
          notifySubscribers();
        }
      }
    } catch (err) {
      console.warn('Error creating shopping list on server:', err);
    }
  }

  return newList;
}

// Rename a shopping list & update purchase date
export async function renameShoppingList(listId: string, newName: string, newPurchaseDate?: string): Promise<void> {
  const list = inMemoryShoppingLists.find(l => l.id === listId);
  if (!list) return;
  
  if (!isListOwner(list)) {
    throw new Error('تنها ایجادکننده لیست اجازه تغییر مشخصات را دارد.');
  }

  list.name = newName.trim();
  if (newPurchaseDate !== undefined) {
    list.purchaseDate = newPurchaseDate;
  }

  await saveShoppingList(list);
}

// Delete a shopping list
export async function deleteShoppingList(listId: string): Promise<void> {
  const list = inMemoryShoppingLists.find(l => l.id === listId);
  if (list && !isListOwner(list)) {
    throw new Error('تنها ایجادکننده لیست اجازه حذف آن را دارد.');
  }

  inMemoryShoppingLists = inMemoryShoppingLists.filter(l => l.id !== listId);
  notifySubscribers();

  const user = getStoredUser();
  if (user) {
    try {
      await apiDelete(`/api/shopping/lists/${listId}`);
    } catch (err) {
      console.warn('Error deleting shopping list from server:', err);
    }
  }
}

// Share list with user by email
export async function shareShoppingListWithEmail(
  listId: string, 
  targetEmailInput: string
): Promise<{ success: boolean; message: string; recipientName?: string }> {
  const cleanEmail = targetEmailInput.trim().toLowerCase();
  
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { success: false, message: 'لطفاً یک آدرس ایمیل معتبر وارد کنید.' };
  }

  const list = inMemoryShoppingLists.find(l => l.id === listId);
  if (!list) {
    return { success: false, message: 'لیست مورد نظر یافت نشد.' };
  }

  if (!isListOwner(list)) {
    return { success: false, message: 'تنها مالک لیست اجازه اشتراک‌گذاری آن را دارد.' };
  }

  if (list.ownerEmail && list.ownerEmail.toLowerCase() === cleanEmail) {
    return { success: false, message: 'این آدرس ایمیل متعلق به خود شماست!' };
  }

  if (list.sharedWithEmails && list.sharedWithEmails.map(e => e.toLowerCase()).includes(cleanEmail)) {
    return { success: false, message: 'این لیست قبلاً با این ایمیل به اشتراک گذاشته شده است.' };
  }

  // Verify that target user exists in database
  const userCheck = await verifyUserEmailExists(cleanEmail);
  if (!userCheck.exists) {
    return { 
      success: false, 
      message: 'کاربری با این آدرس ایمیل در سامانه «پیشرفت» ثبت‌نام نکرده است. جهت حفظ امنیت، اشتراک‌گذاری تنها با کاربران عضو امکان‌پذیر است.' 
    };
  }

  const user = getStoredUser();
  if (user) {
    try {
      const res = await apiPost(`/api/shopping/lists/${listId}/shares`, {
        sharedWithEmail: cleanEmail,
        accessLevel: 'write'
      });
      if (!res.success) {
        return { success: false, message: res.error || 'خطا در ثبت اشتراک‌گذاری.' };
      }
    } catch (err) {
      console.warn('Error sharing list:', err);
      return { success: false, message: 'خطای شبکه در ارسال درخواست اشتراک‌گذاری.' };
    }
  }

  // Update locally
  list.sharedWithEmails = [...new Set([...list.sharedWithEmails, cleanEmail])];
  notifySubscribers();

  return {
    success: true,
    message: `لیست «${list.name}» با موفقیت با ${cleanEmail} به اشتراک گذاشته شد 🎉`,
    recipientName: cleanEmail
  };
}

// Unshare email from list
export async function unshareShoppingListEmail(listId: string, emailToUnshare: string): Promise<void> {
  const cleanEmail = emailToUnshare.trim().toLowerCase();
  const list = inMemoryShoppingLists.find(l => l.id === listId);
  if (!list) return;

  if (!isListOwner(list)) {
    throw new Error('تنها مالک لیست می‌تواند اشتراک را لغو کند.');
  }

  const user = getStoredUser();
  if (user) {
    try {
      // Find the share ID by fetching shares
      const sharesRes = await apiGet<any[]>(`/api/shopping/lists/${listId}/shares`);
      if (sharesRes.success && Array.isArray(sharesRes.data)) {
        const match = sharesRes.data.find(s => (s.shared_with_email || s.email || '').toLowerCase().trim() === cleanEmail);
        if (match) {
          await apiDelete(`/api/shopping/shares/${match.id}`);
        }
      }
    } catch (err) {
      console.warn('Error deleting share on server:', err);
    }
  }

  list.sharedWithEmails = list.sharedWithEmails.filter(e => e.toLowerCase().trim() !== cleanEmail);
  notifySubscribers();
}

// Subscribe to real-time / periodic updates across all shopping lists
export function subscribeToShoppingLists(onUpdate: (lists: ShoppingList[]) => void): () => void {
  shoppingListListeners.add(onUpdate);

  if (inMemoryShoppingLists.length > 0) {
    onUpdate([...inMemoryShoppingLists]);
  }

  fetchShoppingListsFromServer();

  return () => {
    shoppingListListeners.delete(onUpdate);
  };
}

// Full sync trigger for explicit refresh / cold-start
export async function syncShoppingDataFromCloud(): Promise<ShoppingList[]> {
  return await fetchShoppingListsFromServer();
}

// Legacy export functions for backward compatibility with existing code
export function getShoppingItems(): ShoppingItem[] {
  const firstList = inMemoryShoppingLists[0];
  return firstList ? firstList.items : [];
}

export function saveShoppingItems(items: ShoppingItem[]): void {
  if (inMemoryShoppingLists.length === 0) {
    createShoppingList('خرید هفتگی خانه').then(list => {
      list.items = items;
      saveShoppingList(list);
    });
  } else {
    inMemoryShoppingLists[0].items = items;
    saveShoppingList(inMemoryShoppingLists[0]);
  }
}

export function subscribeToShoppingData(onUpdate: (items: ShoppingItem[]) => void) {
  return subscribeToShoppingLists((lists) => {
    if (lists.length > 0) {
      onUpdate(lists[0].items || []);
    }
  });
}
